import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Timeline } from "../components/ui/timeline";
import { Main } from "../theming/styles";
import { useTheme } from "styled-components";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { Nav, AppShowcase } from "../components";
import { SiteFooter } from "../components/foot/SiteFooter";
import { Spread } from "../components/switch/styles";
import { Thumb, Collage1, Collage2, Collage3, Collage4, DubPreview, SevenMinPodcast, CaseStudyPodcast, SafliteMockup } from "../images";
import DiraCover from "../images/dira/dira-cover-image.png";
import KimaniAbout from "../images/kimani_about_me.jpeg";
import ZoomImg from "../components/ZoomImg";
import TestimonialsScroll from "../components/TestimonialsScroll";

/* ── Responsive helpers ─────────────────────────────────────── */
const HeroCard = styled.div`
  border-radius: 24px;
  padding: 56px 56px 48px;
  overflow: hidden;
  background:
    radial-gradient(circle, ${({ theme }) => theme.outline} 1.2px, transparent 1.2px)
    0% 0% / 24px 24px
    ${({ theme }) => theme.altWhite};

  @media (max-width: 768px) {
    padding: 32px 24px 36px;
    border-radius: 20px;
  }
`;

/* Hero actions: one primary, one secondary */
const HeroCtas = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  margin-top: 40px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
    margin-top: 32px;

    .btn-primary { align-self: stretch; justify-content: center; padding: 16px 24px; }
    .btn-text { align-self: center; }
  }
`;

/* Feature cards row — hidden on mobile */
const DesktopCards = styled.div`
  display: flex;
  overflow: visible;
  padding: 24px 0 16px;

  @media (max-width: 768px) {
    display: none;
  }
`;

/* About card wrapper — hidden on mobile */
const AboutCardWrapper = styled.div`
  max-width: 1200px;
  margin: 100px auto;
  padding: 0 24px;

  @media (max-width: 768px) {
    display: none;
  }
`;

/* ── Case studies outer card ── */
const CaseStudiesCard = styled.div`
  background: ${({ theme }) => theme.altWhite};
  border-radius: var(--radius-xl);
`;

/* ── Section heading wrapper ── */
const WorkHeadingWrapper = styled.div`
  margin-bottom: 40px;

  @media (max-width: 768px) {
    padding: 0;
    margin-bottom: 28px;
  }
`;

const CaseCard = styled(Link)`
  display: block;
  text-decoration: none;
  border-radius: 0;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-radius 0.22s ease;

  &:hover {
    transform: translateY(-3px);
    border-radius: 20px;
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.10), 0 2px 6px rgba(0, 0, 0, 0.05);
  }
`;

/* ── Case card text body (heading + description) ── */
const CaseCardBody = styled.div`
  padding: 32px 32px 24px;

  @media (max-width: 768px) {
    padding: 0;
  }
`;

/* ── Case card CTA row (button below image) ── */
const CaseCardCta = styled.div`
  padding: 20px 32px 28px;

  @media (max-width: 768px) {
    padding: 0;
  }
`;

/* ── Case card heading ── */
const CaseCardHeading = styled.h3`
  /* font-family, color inherited from type scale */
  font-size: clamp(22px, 2.5vw, 28px);
  margin: 0 0 12px;
`;

/* ── Case card description ── */
const CaseCardDescription = styled.p`
  font-family: var(--font-body);
  font-size: 16px;
  line-height: 162.5%;
  font-weight: var(--font-weight-regular);
  color: var(--color-text-secondary);
  max-width: 640px;
  margin: 0;
`;

/* ── Case card read button ── */
const CaseCardBtn = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--color-primary);
  font-family: var(--font-ui);
  font-size: 16px;
  font-weight: var(--font-weight-medium);
  transition: gap 0.15s ease;

  a:hover & { gap: 10px; }
`;

/* ── Case study media wrapper — responsive height ── */
const CaseMediaWrapper = styled.div`
  width: 100%;
  overflow: hidden;
  line-height: 0;

  img, video {
    width: 100%;
    height: 450px;
    object-fit: cover;
    display: block;
    border-radius: 12px;
  }

  @media (max-width: 768px) {
    img, video {
      height: 250px;
    }
  }
`;

/* About card two-column layout */
const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 5fr 7fr;
  gap: 48px;
  align-items: center;
  margin-top: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 28px;
    margin-top: 28px;
  }
`;

/* ── Testimonials section ── */
const TestimonialsCard = styled.div`
  background: ${({ theme }) => theme.altWhite};
  border-radius: 24px;
  padding: 52px 56px 0;

  @media (max-width: 768px) {
    padding: 32px 24px 32px;
  }
`;

const TestimonialsCollage = styled.div`
  position: relative;
  height: 500px;
  overflow: hidden;

  @media (max-width: 768px) {
    position: static;
    height: auto;
    overflow: visible;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
`;

const TestimonialCard = styled.div`
  background: ${({ $bg }) => $bg};
  border: 2px solid ${({ $border }) => $border};
  border-radius: 14px;
  padding: 15px 17px;
  position: absolute;

  @media (max-width: 768px) {
    position: static;
    transform: none !important;
    width: 100% !important;
    left: auto !important;
    right: auto !important;
    top: auto !important;
    bottom: auto !important;
    box-sizing: border-box;
  }
`;

/* ── Talks section ── */
const TestimonialsWrap = styled.div`
  background: ${({ theme }) => theme.altWhite};
  border-radius: var(--radius-xl);
`;

const TalksCard = styled.div`
  background: ${({ theme }) => theme.altWhite};
  border-radius: var(--radius-xl);
`;

const TalksGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 28px;
  margin-top: 36px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
    margin-top: 24px;
  }
`;

const TalkFeaturedImage = styled.div`
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 20px;

  img {
    width: 100%;
    height: 310px;
    object-fit: cover;
    display: block;
    border-radius: 12px;
  }

  @media (max-width: 768px) {
    img { height: 200px; }
  }
`;

const TalkSmallImage = styled.div`
  flex-shrink: 0;
  border-radius: 10px;
  overflow: hidden;

  img {
    width: 168px;
    height: 118px;
    object-fit: cover;
    display: block;
    border-radius: 10px;
  }

  @media (max-width: 768px) {
    img { width: 110px; height: 78px; }
  }
`;

const TalkDate = styled.p`
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-meta);
  margin: 0 0 6px 0;
  letter-spacing: -0.01em;
`;

const TalkTitle = styled.h3`
  /* font-family inherited from type scale */
  font-size: clamp(18px, 2vw, 24px);  /* intentional: smaller than h3 scale for card context */
  color: ${({ theme }) => theme.textMain};  /* override: talks card sits on dark bg */
  margin: 0 0 8px;
`;

const TalkDesc = styled.p`
  font-family: var(--font-body);
  font-size: 16px;
  font-weight: 400;
  color: var(--color-text-secondary);
  margin: 0 0 12px 0;
  line-height: 155%;
`;

const TalkTagRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const TalkTag = styled.span`
  display: inline-flex;
  align-items: center;
  background: ${({ theme }) => theme.main};
  color: ${({ theme }) => theme.textMain};
  border: 1px solid ${({ theme }) => theme.outline};
  border-radius: 9999px;
  padding: 4px 12px;
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: 500;
  letter-spacing: -0.01em;
`;

const BeyondGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  margin-top: 36px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 32px;
    margin-top: 24px;
  }
`;

const BeyondSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const BeyondSectionLabel = styled.p`
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-text-meta);
  margin: 0 0 16px;
`;

const BeyondItem = styled.div`
  padding: 20px 0;
  border-top: 1px solid ${({ theme }) => theme.outline};

  &:last-child {
    border-bottom: 1px solid ${({ theme }) => theme.outline};
  }
`;

const BeyondItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 4px;
`;

const BeyondItemTitle = styled.p`
  font-family: var(--font-body);
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.textMain};
  margin: 0;
  letter-spacing: -0.01em;
`;

const BeyondItemMeta = styled.p`
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--color-text-meta);
  margin: 0 0 10px;
`;

const BeyondItemDesc = styled.p`
  font-family: var(--font-body);
  font-size: 16px;
  color: var(--color-text-secondary);
  line-height: 1.6;
  margin: 0 0 10px;
`;

const BeyondLink = styled.a`
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: 600;
  color: var(--color-primary);
  text-decoration: underline;
  text-underline-offset: 3px;
  letter-spacing: -0.01em;

  &:hover { opacity: 0.75; }
`;

/* ── Option label chip ── */
const OptionLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--color-primary);
  color: #fff;
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 4px 12px;
  border-radius: 9999px;
  margin-bottom: 16px;
`;

/* ── Option A: Bento Grid ── */
const BentoGrid = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  grid-template-rows: auto auto auto;
  gap: 16px;
  margin-top: 32px;

  /* Tablet landscape: equal columns */
  @media (max-width: 1024px) and (min-width: 901px) {
    grid-template-columns: 1fr 1fr;
  }

  /* Tablet portrait + mobile: single column */
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const BentoTile = styled.div`
  background: transparent;
  border-radius: 20px;
  padding: 24px;
  border: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: default;

  &:hover {
    transform: scale(1.02);
  }

  @media (max-width: 900px) {
    padding: 20px 24px;
  }

  @media (max-width: 768px) {
    padding: 16px 20px;
  }
`;

const BentoTalksCard = styled(BentoTile)`
  grid-column: 1;
  grid-row: 1 / 4;
  display: flex;
  flex-direction: column;
  gap: 40px;

  @media (max-width: 1024px) {
    grid-row: auto;
  }

  @media (max-width: 900px) {
    grid-column: 1;
    grid-row: auto;
    gap: 32px;
  }
`;

const TalkRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;

  @media (max-width: 900px) {
    gap: 14px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const TalkThumb = styled.img`
  width: 200px;
  height: 200px;
  object-fit: cover;
  border-radius: 12px;
  flex-shrink: 0;

  @media (max-width: 1024px) and (min-width: 901px) {
    width: 140px;
    height: 140px;
  }

  @media (max-width: 900px) {
    width: 120px;
    height: 120px;
  }

  @media (max-width: 768px) {
    width: 100%;
    height: 180px;
  }
`;

const BentoTileTalk = styled(BentoTile)`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const BentoCategoryPill = styled.span`
  display: inline-block;
  width: fit-content;
  background: rgba(17, 19, 24, 0.06);
  color: var(--color-text-meta);
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 3px 10px;
  border-radius: 9999px;
  margin-bottom: 10px;
`;

const BentoTitle = styled.p`
  font-family: var(--font-body);
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.textMain};
  margin: 0 0 6px;
  letter-spacing: -0.01em;
`;

const BentoMeta = styled.p`
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--color-text-meta);
  margin: 0 0 8px;
`;

const BentoDesc = styled.p`
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--color-text-secondary);
  line-height: 1.6;
  margin: 0 0 10px;
`;

/* ── Option B: Tab Switcher ── */
const TabBar = styled.div`
  display: flex;
  gap: 4px;
  margin: 24px 0 32px;
  background: ${({ theme }) => theme.main};
  border: 1px solid ${({ theme }) => theme.outline};
  border-radius: 9999px;
  padding: 4px;
  width: fit-content;
`;

const Tab = styled.button`
  all: unset;
  cursor: pointer;
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  padding: 8px 20px;
  border-radius: 9999px;
  background: ${({ $active, theme }) => $active ? 'var(--color-primary)' : 'transparent'};
  color: ${({ $active, theme }) => $active ? '#fff' : theme.textMain};
  opacity: ${({ $active }) => $active ? 1 : 0.55};
  transition: background 0.2s ease, color 0.2s ease, opacity 0.2s ease;
  white-space: nowrap;

  &:hover { opacity: 1; }
`;

const TabContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const TabItem = styled.div`
  display: flex;
  gap: 20px;
  align-items: flex-start;
  padding: 20px 0;
  border-top: 1px solid ${({ theme }) => theme.outline};

  &:last-child { border-bottom: 1px solid ${({ theme }) => theme.outline}; }

  @media (max-width: 640px) { flex-direction: column; gap: 12px; }
`;

const TabItemLeft = styled.div`
  flex: 1;
  min-width: 0;
`;

const TabItemRight = styled.div`
  flex: 2;
  min-width: 0;
`;

/* ── Option C: Timeline / CV row ── */
const TimelineList = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 32px;
`;

const TimelineRow = styled.a`
  display: grid;
  grid-template-columns: 120px 1fr 1.6fr;
  gap: 24px;
  align-items: start;
  padding: 20px 0;
  border-top: 1px solid ${({ theme }) => theme.outline};
  text-decoration: none;
  cursor: default;
  transition: background 0.15s ease;
  border-radius: 8px;

  &:last-child { border-bottom: 1px solid ${({ theme }) => theme.outline}; }
  &:hover { background: ${({ theme }) => theme.main}; padding-left: 12px; padding-right: 12px; }

  @media (max-width: 640px) {
    grid-template-columns: 80px 1fr;
    grid-template-rows: auto auto;
  }
`;

const TimelineCategoryBadge = styled.span`
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-text-meta);
  padding-top: 3px;
`;

const TimelineTitle = styled.p`
  font-family: var(--font-body);
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.textMain};
  margin: 0 0 4px;
  letter-spacing: -0.01em;
`;

const TimelineMeta = styled.p`
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--color-text-meta);
  margin: 0;
`;

const TimelineDesc = styled.p`
  font-family: var(--font-body);
  font-size: 16px;
  color: var(--color-text-secondary);
  line-height: 1.65;
  margin: 0 0 8px;

  @media (max-width: 640px) {
    grid-column: 1 / -1;
  }
`;

const talks = [
    {
        date: "Aug 08, 2022",
        image: SevenMinPodcast,
        title: "7min Product Master Series",
        description: "How to derive user needs from research, leverage technology to satisfy them, and think critically about constraints so solutions scale.",
        tags: ["Product Design", "Technology"],
        href: "https://creators.spotify.com/pod/profile/7min-product-master-serie/episodes/Godfrey-Kimani--Product-Designer--Safaricom-PLC-e285thc/a-aa8a7tj",
    },
    {
        date: "Jul 03, 2026",
        image: CaseStudyPodcast,
        title: "How to make your design portfolio stand out",
        description: "UXD Weekly Catchup — practical strategies for crafting a portfolio that communicates your design thinking and gets you noticed.",
        tags: ["Portfolio", "Product Design"],
    },
];

const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) {
        const y = el.getBoundingClientRect().top + window.pageYOffset - 96;
        window.scrollTo({ top: y, behavior: 'smooth' });
    }
};

const TABS = ['Talks', 'Mentorship', 'Community'];

const TabSwitcher = ({ talks, openModal, theme }) => {
    const [activeTab, setActiveTab] = React.useState('Talks');
    return (
        <>
            <TabBar>
                {TABS.map(t => (
                    <Tab key={t} $active={activeTab === t} onClick={() => setActiveTab(t)}>{t}</Tab>
                ))}
            </TabBar>
            <TabContent>
                {activeTab === 'Talks' && talks.map((talk, i) => (
                    <TabItem key={i}>
                        <TabItemLeft>
                            <TalkSmallImage>
                                <ZoomImg src={talk.image} alt={talk.title} onClick={(e) => openModal(talk.image, e)} />
                            </TalkSmallImage>
                        </TabItemLeft>
                        <TabItemRight>
                            <TalkDate>{talk.date}</TalkDate>
                            <BeyondItemTitle style={{ marginBottom: 6 }}>{talk.title}</BeyondItemTitle>
                            <BeyondItemDesc>{talk.description}</BeyondItemDesc>
                            <TalkTagRow style={{ marginBottom: 10 }}>
                                {talk.tags.map(tag => <TalkTag key={tag}>{tag}</TalkTag>)}
                            </TalkTagRow>
                            {talk.href && <BeyondLink href={talk.href} target="_blank" rel="noopener noreferrer">Listen →</BeyondLink>}
                        </TabItemRight>
                    </TabItem>
                ))}
                {activeTab === 'Mentorship' && <>
                    <TabItem>
                        <TabItemLeft>
                            <BeyondItemTitle>ADPList mentor</BeyondItemTitle>
                            <BeyondItemMeta>71 sessions · 50+ hrs</BeyondItemMeta>
                        </TabItemLeft>
                        <TabItemRight>
                            <BeyondItemDesc>1:1 sessions with designers across Africa and beyond, focused on fintech UX, portfolio reviews, and navigating senior and lead roles in emerging-market product teams.</BeyondItemDesc>
                            <BeyondLink href="https://adplist.org/mentors/godfrey-kimani" target="_blank" rel="noopener noreferrer">Book a session →</BeyondLink>
                        </TabItemRight>
                    </TabItem>
                    <TabItem>
                        <TabItemLeft>
                            <BeyondItemTitle>From mentee to peer mentor</BeyondItemTitle>
                            <BeyondItemMeta>KamiLimu</BeyondItemMeta>
                        </TabItemLeft>
                        <TabItemRight>
                            <BeyondItemDesc>Started as a mentee in Cohort 4.0, the programme that introduced me to design as a path. Returned the following year as a peer mentor for the next cohort.</BeyondItemDesc>
                        </TabItemRight>
                    </TabItem>
                </>}
                {activeTab === 'Community' && <>
                    <TabItem>
                        <TabItemLeft>
                            <BeyondItemTitle>Friends of Figma, Nairobi</BeyondItemTitle>
                            <BeyondItemMeta>Regular contributor</BeyondItemMeta>
                        </TabItemLeft>
                        <TabItemRight>
                            <BeyondItemDesc>Plugged into the local design community, attending events and conversations on product design practice in Kenya.</BeyondItemDesc>
                        </TabItemRight>
                    </TabItem>
                    <TabItem>
                        <TabItemLeft>
                            <BeyondItemTitle>Interaction Design Foundation (IxDF)</BeyondItemTitle>
                            <BeyondItemMeta>Certified member</BeyondItemMeta>
                        </TabItemLeft>
                        <TabItemRight>
                            <BeyondItemDesc>Member of the world's largest design education institution: evidence-based curricula taught by industry leaders, with local chapters across 105 countries.</BeyondItemDesc>
                            <BeyondLink href="https://www.interaction-design.org" target="_blank" rel="noopener noreferrer">Visit IxDF →</BeyondLink>
                        </TabItemRight>
                    </TabItem>
                </>}
            </TabContent>
        </>
    );
};

/* ── Scroll-driven timeline fill line ── */

const Landing = ({ toggleMode, mode, spread, setDisableScroll }) => {
    const theme = useTheme();
    const spreadClass =
        spread === "first" ? "growBlack" : spread === "second" ? "growWhite" : "";
    const [hoveredCard, setHoveredCard] = useState(null);
    const [modalImg, setModalImg] = useState(null);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') setModalImg(null); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    useEffect(() => {
        document.body.style.overflow = modalImg ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [modalImg]);


    const openModal = (src, e) => {
        if (e) e.stopPropagation();
        setModalImg(src);
    };

    // ── Testimonial drag ──────────────────────────────────────────
    const [dragOffsets, setDragOffsets] = useState(() =>
        Object.fromEntries(Array.from({ length: 7 }, (_, i) => [i, { dx: 0, dy: 0 }]))
    );
    const [draggingId, setDraggingId] = useState(null);
    const dragRef = useRef(null); // { id, startX, startY, origDx, origDy }

    useEffect(() => {
        const onMove = (e) => {
            if (!dragRef.current) return;
            if (e.cancelable) e.preventDefault();
            const { id, startX, startY, origDx, origDy } = dragRef.current;
            const clientX = e.touches ? e.touches[0].clientX : e.clientX;
            const clientY = e.touches ? e.touches[0].clientY : e.clientY;
            setDragOffsets(prev => ({
                ...prev,
                [id]: { dx: clientX - startX + origDx, dy: clientY - startY + origDy },
            }));
        };
        const onUp = () => {
            if (!dragRef.current) return;
            dragRef.current = null;
            setDraggingId(null);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchmove', onMove, { passive: false });
        window.addEventListener('touchend', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onUp);
        };
    }, []);

    const startDrag = (id, e) => {
        e.preventDefault();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        dragRef.current = { id, startX: clientX, startY: clientY, origDx: dragOffsets[id].dx, origDy: dragOffsets[id].dy };
        setDraggingId(id);
    };

    // Merges base card style with live drag offset + drag UX
    const ds = (id, base) => ({
        ...base,
        transform: `translate(${dragOffsets[id].dx}px, ${dragOffsets[id].dy}px) ${base.transform ?? ''}`,
        zIndex: draggingId === id ? 20 : (base.zIndex ?? 4),
        cursor: draggingId === id ? 'grabbing' : 'grab',
        userSelect: 'none',
        touchAction: 'none',
        boxShadow: draggingId === id ? '0 20px 56px rgba(0,0,0,0.22)' : undefined,
        transition: draggingId === id ? 'box-shadow 0.15s ease' : 'box-shadow 0.2s ease',
    });


    // Push-apart transform: hovered card flattens+scales; all others slide away
    const cardOrder = ['peach', 'video', 'green', 'lavender'];
    const getCardTransform = (id, deg) => {
        if (hoveredCard === id) return `rotate(0deg) scale(1.04)`;
        if (hoveredCard) {
            const myIdx = cardOrder.indexOf(id);
            const hIdx  = cardOrder.indexOf(hoveredCard);
            const dist  = myIdx - hIdx;             // negative = left of hovered
            const dir   = dist < 0 ? -1 : 1;
            const px    = Math.abs(dist) * 72;      // 72px per step — scales with distance
            return `rotate(${deg}deg) translateX(${dir * px}px)`;
        }
        return `rotate(${deg}deg)`;
    };

    // Dim + blur cards that are not in focus
    const getCardDim = (id) => {
        if (!hoveredCard || hoveredCard === id) return { opacity: 1, filter: 'none' };
        return { opacity: 0.7, filter: 'blur(1px)' };
    };

    // ── Shared card sub-element styles ──────────────────────────────
    const cardHeading = {
        fontFamily: "'Clash Display', 'Inter', sans-serif",
        fontSize: 21,
        fontWeight: 600,
        color: 'var(--color-heading)',
        margin: '0 0 10px 0',
        lineHeight: '125%',
        letterSpacing: '-0.5px',
    };
    const cardCta = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: '#000',
        color: '#fff',
        border: 'none',
        borderRadius: 40,
        padding: '10px 18px',
        fontFamily: 'var(--font-body)',
        fontSize: 13,
        fontWeight: 500,
        cursor: 'pointer',
        alignSelf: 'flex-start',
        marginTop: 22,
        letterSpacing: '-0.01em',
        textDecoration: 'none',
    };
    const cardBase = {
        borderRadius: 18,
        padding: '28px 24px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        minHeight: 320,
        cursor: 'pointer',
        position: 'relative',
        transformOrigin: 'bottom center',
        transition: 'transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.25s ease, filter 0.25s ease',
        boxShadow: 'rgba(0,0,0,0) 0px 0px 0px 0px, rgba(0,0,0,0) 0px 0px 0px 0px, rgba(0,0,0,0) 0px 0px 0px 0px, oklab(0 0 0 / 0.05) 0px 0px 0px 1px, rgba(16,24,40,0.08) 0px 4px 16px 0px',
    };

    return (
        <Main style={{
            position: "relative",
            height: setDisableScroll ? "100vh" : undefined,
            overflow: setDisableScroll ? "hidden" : undefined,
        }}>
            <Spread className={spreadClass} />
            <Nav mode={mode} toggleMode={toggleMode} spread={spread} />


            {/* ── Card 1: hero + feature cards ── */}
            <div className="section-wrap">
                <HeroCard>
                    {/* Hero text */}
                    <div>
                        <h1>
                            Hi, I'm Godfrey.<br />
                            Lead Product Designer.
                        </h1>
                        <p className="text-style" style={{ maxWidth: 560, fontSize: 16 }}>
                            Currently at{' '}
                            <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 5,
                                background: '#E4F0E9',
                                color: 'var(--color-heading)',
                                padding: '3px 11px',
                                borderRadius: 20,
                                fontSize: 13,
                                fontWeight: 600,
                                letterSpacing: '-0.01em',
                                verticalAlign: 'middle',
                            }}>
                                Safaricom PLC
                            </span>
                            {' '}— crafting elegant, humane and scalable digital products with 10+ years of experience.
                        </p>
                    </div>

                    <HeroCtas>
                        <button className="btn-primary" onClick={() => scrollToId('case-studies')}>
                            See case studies →
                        </button>
                        <Link to="/about" className="btn-text">
                            About me →
                        </Link>
                    </HeroCtas>

                </HeroCard>
            </div>

            {/* ── Feature cards: Recent work | Video intro | Shipped | Talks ── */}
            <div className="section-wrap">
                <DesktopCards>

                    {/* PeachCard — Recent work (+5° → 0° on hover, pushes neighbours) */}
                    <div
                        id="peach-card"
                        onMouseEnter={() => setHoveredCard('peach')}
                        onMouseLeave={() => setHoveredCard(null)}
                        style={{
                            ...cardBase,
                            ...getCardDim('peach'),
                            background: '#F5D9C0',
                            flex: 1,
                            zIndex: 3,
                            transform: getCardTransform('peach', 5),
                        }}
                    >
                        {/* Thumbnail cluster */}
                        <div style={{
                            position: 'absolute',
                            top: -46,
                            left: 0,
                            right: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'flex-end',
                            pointerEvents: 'none',
                            opacity: hoveredCard === 'peach' ? 1 : 0,
                            transform: hoveredCard === 'peach' ? 'translateY(0px)' : 'translateY(10px)',
                            transition: 'opacity 0.28s ease, transform 0.28s ease',
                            zIndex: 10,
                        }}>
                            <img src={Thumb} alt="" style={{ width: 82, height: 58, objectFit: 'cover', borderRadius: 8, transform: 'rotate(-9deg) translateY(7px)', boxShadow: '0 4px 14px rgba(0,0,0,0.22)', zIndex: 1, flexShrink: 0 }} />
                            <img src={DubPreview} alt="" style={{ width: 90, height: 66, objectFit: 'cover', borderRadius: 8, transform: 'rotate(2deg)', boxShadow: '0 6px 18px rgba(0,0,0,0.26)', zIndex: 2, marginLeft: -16, flexShrink: 0 }} />
                            <img src={DiraCover} alt="" style={{ width: 82, height: 58, objectFit: 'cover', borderRadius: 8, transform: 'rotate(10deg) translateY(7px)', boxShadow: '0 4px 14px rgba(0,0,0,0.22)', zIndex: 1, marginLeft: -16, flexShrink: 0 }} />
                        </div>

                        <div>
                            <h3 style={cardHeading}>Recent work</h3>
                            <p className="text-style">See how I turn messy product problems into shipped experiences.</p>
                        </div>
                        <button onClick={() => scrollToId('case-studies')} style={cardCta}>
                            View My Work →
                        </button>
                    </div>

                    {/* VideoCard — placeholder (-5° → 0° on hover, pushes neighbours) */}
                    <div
                        id="video-card"
                        onMouseEnter={() => setHoveredCard('video')}
                        onMouseLeave={() => setHoveredCard(null)}
                        style={{
                            ...cardBase,
                            background: '#111111',
                            ...getCardDim('video'),
                            flex: 1,
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 2,
                            marginLeft: 40,
                            transform: getCardTransform('video', -5),
                        }}
                    >
                        {/* Hover annotation — floats above card */}
                        <div style={{
                            position: 'absolute',
                            top: -58,
                            left: '50%',
                            transform: hoveredCard === 'video'
                                ? 'translateX(-50%) translateY(0px)'
                                : 'translateX(-50%) translateY(-6px)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                            opacity: hoveredCard === 'video' ? 1 : 0,
                            transition: 'opacity 0.25s ease, transform 0.25s ease',
                            zIndex: 10,
                        }}>
                            <span style={{
                                fontFamily: "'Caveat', cursive",
                                fontSize: 21,
                                fontWeight: 700,
                                color: '#1e37ee',
                                lineHeight: 1,
                            }}>
                                In case you are tired of reading
                            </span>
                            {/* Curved fishhook arrow: sweeps right then down */}
                            <svg width="34" height="38" viewBox="0 0 34 38" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
                                <path
                                    d="M4 6 C4 2, 30 2, 30 16 C30 26, 22 33, 16 35"
                                    stroke="#1e37ee"
                                    strokeWidth="2.2"
                                    strokeLinecap="round"
                                    fill="none"
                                />
                                {/* Arrowhead at bottom pointing down-left */}
                                <path
                                    d="M10 31 L16 36 L20 30"
                                    stroke="#1e37ee"
                                    strokeWidth="2.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    fill="none"
                                />
                            </svg>
                        </div>
                        <div style={{
                            position: 'absolute',
                            inset: 0,
                            borderRadius: 18,
                            background: 'radial-gradient(ellipse at 50% 40%, #2c2c2c 0%, #0e0e0e 100%)',
                        }} />
                        <div style={{
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 12,
                        }}>
                            <div style={{
                                width: 52,
                                height: 52,
                                borderRadius: '50%',
                                background: 'rgba(255,255,255,0.1)',
                                border: '1.5px solid rgba(255,255,255,0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                                    <path d="M8 5v14l11-7z" />
                                </svg>
                            </div>
                            <span style={{
                                fontFamily: 'var(--font-body)',
                                fontSize: 13,
                                fontWeight: 500,
                                color: 'rgba(255,255,255,0.65)',
                                letterSpacing: '-0.01em',
                            }}>Video intro</span>
                            <span style={{
                                fontFamily: 'var(--font-body)',
                                fontSize: 10,
                                fontWeight: 500,
                                color: 'rgba(255,255,255,0.3)',
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                            }}>Coming soon</span>
                        </div>
                    </div>

                    {/* GreenCard — Shopped Apps (+5° → 0° on hover, pushes neighbours) */}
                    <div
                        id="green-card"
                        onMouseEnter={() => setHoveredCard('green')}
                        onMouseLeave={() => setHoveredCard(null)}
                        style={{
                            ...cardBase,
                            background: '#C8E8D4',
                            ...getCardDim('green'),
                            flex: 1,
                            zIndex: 1,
                            marginLeft: 8,
                            transform: getCardTransform('green', 5),
                        }}
                    >
                        {/* App icon fan — 4 square icons fanned above the card */}
                        <div style={{
                            position: 'absolute',
                            top: -38,
                            left: 0,
                            right: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'flex-end',
                            pointerEvents: 'none',
                            opacity: hoveredCard === 'green' ? 1 : 0,
                            transform: hoveredCard === 'green' ? 'translateY(0px)' : 'translateY(10px)',
                            transition: 'opacity 0.28s ease, transform 0.28s ease',
                            zIndex: 10,
                        }}>
                            {[
                                { src: Collage1, rot: -18, tx: 0,  ty: 8, z: 1 },
                                { src: Collage2, rot: -6,  tx: -4, ty: 2, z: 2 },
                                { src: Collage3, rot: 6,   tx: -4, ty: 2, z: 3 },
                                { src: Collage4, rot: 18,  tx: 0,  ty: 8, z: 2 },
                            ].map(({ src, rot, tx, ty, z }, i) => (
                                <img
                                    key={i}
                                    src={src}
                                    alt=""
                                    style={{
                                        width: 52,
                                        height: 52,
                                        objectFit: 'cover',
                                        borderRadius: 14,
                                        transform: `rotate(${rot}deg) translate(${tx}px, ${ty}px)`,
                                        boxShadow: '0 4px 14px rgba(0,0,0,0.22)',
                                        zIndex: z,
                                        marginLeft: i === 0 ? 0 : -8,
                                        flexShrink: 0,
                                        border: '2px solid rgba(255,255,255,0.8)',
                                    }}
                                />
                            ))}
                        </div>

                        <div>
                            <h3 style={cardHeading}>Shipped Experiences</h3>
                            <p className="text-style">Designed and shipped 20+ end-to-end user experiences fueled by a continuous drive for product innovation.</p>
                        </div>
                        <button onClick={() => scrollToId('side-projects')} style={cardCta}>What I've Shipped →</button>
                    </div>

                    {/* LavenderCard — Public talks (-5° → 0° on hover, pushes neighbours) */}
                    <div
                        id="lavender-card"
                        onMouseEnter={() => setHoveredCard('lavender')}
                        onMouseLeave={() => setHoveredCard(null)}
                        style={{
                            ...cardBase,
                            background: '#E2DBF5',
                            ...getCardDim('lavender'),
                            flex: 1,
                            zIndex: 0,
                            marginLeft: 40,
                            transform: getCardTransform('lavender', -5),
                        }}
                    >
                        {/* Thumbnail cluster */}
                        <div style={{
                            position: 'absolute',
                            top: -46,
                            left: 0,
                            right: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'flex-end',
                            pointerEvents: 'none',
                            opacity: hoveredCard === 'lavender' ? 1 : 0,
                            transform: hoveredCard === 'lavender' ? 'translateY(0px)' : 'translateY(10px)',
                            transition: 'opacity 0.28s ease, transform 0.28s ease',
                            zIndex: 10,
                        }}>
                            <img src={SafliteMockup} alt="" style={{ width: 82, height: 58, objectFit: 'cover', borderRadius: 8, transform: 'rotate(-9deg) translateY(7px)', boxShadow: '0 4px 14px rgba(0,0,0,0.22)', zIndex: 1, flexShrink: 0 }} />
                            <img src={DubPreview} alt="" style={{ width: 90, height: 66, objectFit: 'cover', borderRadius: 8, transform: 'rotate(2deg)', boxShadow: '0 6px 18px rgba(0,0,0,0.26)', zIndex: 2, marginLeft: -16, flexShrink: 0 }} />
                            <img src={Thumb} alt="" style={{ width: 82, height: 58, objectFit: 'cover', borderRadius: 8, transform: 'rotate(10deg) translateY(7px)', boxShadow: '0 4px 14px rgba(0,0,0,0.22)', zIndex: 1, marginLeft: -16, flexShrink: 0 }} />
                        </div>

                        <div>
                            <h3 style={cardHeading}>Public talks</h3>
                            <p className="text-style">I speak about design, growth, AI, and the craft behind building better products.</p>
                        </div>
                        <a href="#talks" style={cardCta} onClick={e => { e.preventDefault(); document.getElementById('talks')?.scrollIntoView({ behavior: 'smooth' }); }}>
                            Explore My Talks →
                        </a>
                    </div>

                </DesktopCards>
            </div>

            {/* ── Card 3: case studies ── */}
            <div className="lp-section">
                <CaseStudiesCard id="case-studies" className="card-pad">
                        <WorkHeadingWrapper id="work-section-header">
                            <h2 style={{ margin: '0 0 6px' }}>
                                Some recent work
                            </h2>
                            <p className="text-style text-meta" style={{ fontSize: 13 }}>
                                (from full-time jobs)
                            </p>
                        </WorkHeadingWrapper>

                        <Timeline data={[
                            {
                                title: '2024',
                                content: (
                                    <CaseCard id="cs-b2b" to="/dubi">
                                        <CaseCardBody id="cs-b2b-body">
                                            <CaseCardHeading>Dubi — Survey & Feedback Platform</CaseCardHeading>
                                            <CaseCardDescription>Turned 50M+ Safaricom users into a listening machine — 92M conversations across Kenya & Ethiopia in 3 months.</CaseCardDescription>
                                        </CaseCardBody>
                                        <CaseMediaWrapper>
                                            <img src={DubPreview} alt="Dubi Survey & Feedback Platform" style={{ objectFit: 'cover', objectPosition: 'top' }} />
                                        </CaseMediaWrapper>
                                        <CaseCardCta id="cs-b2b-cta">
                                            <CaseCardBtn>Read case study →</CaseCardBtn>
                                        </CaseCardCta>
                                    </CaseCard>
                                ),
                            },
                            {
                                title: '2025',
                                content: (
                                    <CaseCard id="cs-portal" to="/partner-portal">
                                        <CaseCardBody id="cs-portal-body">
                                            <CaseCardHeading>My Safaricom Partner Portal Redesign</CaseCardHeading>
                                            <CaseCardDescription>Unified a fragmented dealer ecosystem into a single SaaS portal — 90M KES in orders, month one.</CaseCardDescription>
                                        </CaseCardBody>
                                        <CaseMediaWrapper>
                                            <img src={Thumb} alt="Safaricom Partner Portal" style={{ objectFit: 'cover', objectPosition: 'top' }} />
                                        </CaseMediaWrapper>
                                        <CaseCardCta id="cs-portal-cta">
                                            <CaseCardBtn>Read case study →</CaseCardBtn>
                                        </CaseCardCta>
                                    </CaseCard>
                                ),
                            },
                            {
                                title: '2025',
                                content: (
                                    <CaseCard id="cs-dira" to="/dira">
                                        <CaseCardBody id="cs-dira-body">
                                            <CaseCardHeading>From Fragmentation to a System 100+ People Actually Use</CaseCardHeading>
                                            <CaseCardDescription>How I led the rebuild of Safaricom's design system — unifying tokens, components, and documentation across products to cut handoff friction and ship faster.</CaseCardDescription>
                                        </CaseCardBody>
                                        <CaseMediaWrapper>
                                            <img src={DiraCover} alt="Dira Design System" style={{ objectFit: 'cover', objectPosition: 'top' }} />
                                        </CaseMediaWrapper>
                                        <CaseCardCta id="cs-dira-cta">
                                            <CaseCardBtn>Read case study →</CaseCardBtn>
                                        </CaseCardCta>
                                    </CaseCard>
                                ),
                            },
                            {
                                title: '2025',
                                content: (
                                    <CaseCard id="cs-web-journey" to="/web-journey">
                                        <CaseCardBody id="cs-web-journey-body">
                                            <CaseCardHeading>Mysaflite App — Unifying the One App Lite web experience for 1M+ customers.</CaseCardHeading>
                                            <CaseCardDescription>End-to-end redesign of the My Safaricom web experience — simplifying navigation, unifying flows, and removing friction for millions of customers.</CaseCardDescription>
                                        </CaseCardBody>
                                        <CaseMediaWrapper>
                                            <img src={SafliteMockup} alt="Mysaflite App" style={{ objectFit: 'cover', objectPosition: 'top' }} />
                                        </CaseMediaWrapper>
                                        <CaseCardCta id="cs-web-journey-cta">
                                            <CaseCardBtn>Read case study →</CaseCardBtn>
                                        </CaseCardCta>
                                    </CaseCard>
                                ),
                            },
                        ]} />
                </CaseStudiesCard>
            </div>

            {/* ── Card 2: about me ── */}
            <AboutCardWrapper id="about">
                <div className="card-pad" style={{
                    background: theme.altWhite,
                    borderRadius: 'var(--radius-xl)',
                }}>
                    {/* Label */}
                    <h5 style={{ margin: '0 0 16px' }}>A bit about me</h5>

                    {/* Heading */}
                    <h2 style={{ margin: 0, maxWidth: 720 }}>
                        Design is how I think.<br />
                        Building is how I prove it.
                    </h2>

                    {/* Two-column: photo + text */}
                    <AboutGrid>
                        {/* Left: photo */}
                        <div style={{
                            overflow: 'hidden',
                            borderRadius: 8,
                            height: 217,
                            border: '8px solid #ede7de',
                        }}>
                            <img
                                src={KimaniAbout}
                                alt="Godfrey presenting"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    objectPosition: 'center 65%',
                                    display: 'block',
                                }}
                            />
                        </div>

                        {/* Right: bio + CTA */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
                            <p className="text-style">
                                With a Graphic design background and 10+ years of craft, I've always sat at the intersection of design and engineering. I don't just hand off specs — I shape products end to end, from messy research to shipped features.
                            </p>
                            <Link to="/about" className="btn-text" style={{ alignSelf: 'flex-start' }}>
                                More about me →
                            </Link>
                        </div>
                    </AboutGrid>
                </div>
            </AboutCardWrapper>

            {/* ── Some other things I do ── */}
            <div className="lp-section" id="side-projects">
                <AppShowcase header={
                    <div style={{ marginBottom: 40 }}>
                        <h5 style={{ margin: '0 0 16px' }}>Outside of work</h5>
                        <h2 style={{ margin: '0 0 16px' }}>I build apps. A lot of apps.</h2>
                        <p className="text-style" style={{ maxWidth: 620 }}>
                            Something I get asked a lot is: why do you have so much time outside of work? My answer is: I have no idea.
                        </p>
                    </div>
                } />
            </div>

            {/* ── Card 4: Talks / Beyond the work ── */}
            <div className="lp-section">
                <TalksCard id="talks" className="card-pad">
                    <div>
                        <h5 style={{ margin: '0 0 16px' }}>Beyond the work</h5>
                        <h2 style={{ margin: 0 }}>Speaking, mentorship, and community.</h2>
                    </div>
                    <BentoGrid>
                        {/* Left: all talks in one tall card */}
                        <BentoTalksCard>
                            <BentoCategoryPill>Talks</BentoCategoryPill>
                            {talks.map((talk, i) => (
                                <TalkRow key={i}>
                                    <TalkThumb src={talk.image} alt={talk.title} />
                                    <div style={{ flex: 1 }}>
                                        <TalkDate>{talk.date}</TalkDate>
                                        <BentoTitle>{talk.title}</BentoTitle>
                                        <BentoDesc>{talk.description}</BentoDesc>
                                        <TalkTagRow style={{ marginBottom: 10 }}>
                                            {talk.tags.map(tag => <TalkTag key={tag}>{tag}</TalkTag>)}
                                        </TalkTagRow>
                                        {talk.href && <BeyondLink href={talk.href} target="_blank" rel="noopener noreferrer">Listen →</BeyondLink>}
                                    </div>
                                </TalkRow>
                            ))}
                        </BentoTalksCard>

                        {/* Right: Mentorship + Community tiles */}
                        <BentoTile>
                            <BentoCategoryPill>Mentorship</BentoCategoryPill>
                            <BentoTitle>Mentorship</BentoTitle>
                            <BentoMeta>71 sessions · 50+ hours · Kenya, Uganda & the US</BentoMeta>
                            <BentoDesc>Led Safaricom's Design/UX Chapter into one of the company's top-recognized chapters, while mentoring 6 designers — including 3 interns promoted to mid-level — via 1:1s on fintech UX, portfolio reviews, and navigating senior roles in emerging-market teams.</BentoDesc>
                            <BeyondLink href="https://adplist.org/mentors/godfrey-kimani" target="_blank" rel="noopener noreferrer">Book a session →</BeyondLink>
                        </BentoTile>
                        <BentoTile style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                            <div>
                                <BentoCategoryPill>Community</BentoCategoryPill>
                                <BentoTitle>Friends of Figma, Nairobi</BentoTitle>
                                <BentoMeta>Regular contributor</BentoMeta>
                                <BentoDesc>Plugged into the local design community, attending events and conversations on product design practice in Kenya.</BentoDesc>
                            </div>
                            <div style={{ borderTop: '1px solid rgba(128,128,128,0.15)', paddingTop: 20, marginTop: 20 }}>
                                <BentoTitle>Interaction Design Foundation (IxDF)</BentoTitle>
                                <BentoMeta>Certified member</BentoMeta>
                                <BentoDesc>Member of the world's largest design education institution: evidence-based curricula taught by industry leaders, with local chapters across 105 countries.</BentoDesc>
                                <BeyondLink href="https://www.interaction-design.org" target="_blank" rel="noopener noreferrer">Visit IxDF →</BeyondLink>
                            </div>
                        </BentoTile>
                    </BentoGrid>
                </TalksCard>
            </div>

            {/* ── Card 5: Testimonials ── */}
            <div className="lp-section">
                <TestimonialsWrap className="card-pad">
                    <TestimonialsScroll />
                </TestimonialsWrap>
            </div>

            {/* ── Footer ── */}
            <SiteFooter />

            {/* Image modal */}
            {modalImg && (
                <div
                    onClick={() => setModalImg(null)}
                    style={{
                        position: 'fixed', inset: 0, zIndex: 9999,
                        background: 'rgba(0,0,0,0.72)',
                        backdropFilter: 'blur(8px)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: 24, cursor: 'zoom-out',
                    }}
                >
                    <img
                        src={modalImg}
                        alt="Expanded view"
                        style={{
                            maxWidth: '90vw', maxHeight: '88vh',
                            borderRadius: 16,
                            boxShadow: '0 32px 80px rgba(0,0,0,0.5)',
                        }}
                    />
                    <button
                        onClick={() => setModalImg(null)}
                        style={{
                            position: 'absolute', top: 20, right: 20,
                            width: 36, height: 36, borderRadius: '50%',
                            background: 'rgba(255,255,255,0.15)',
                            border: 'none', color: '#fff', fontSize: 18,
                            cursor: 'pointer', display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                        }}
                    >✕</button>
                </div>
            )}
        </Main>
    );
};

export { Landing };
